import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, catchError, map, Observable, of, shareReplay, tap} from 'rxjs';
import {apiUrl} from '../../environments/environment';
import {
  AccessPolicyContext,
  AccessPolicyDecision,
  AccessPolicyDto,
  AccessPolicyLogicNode,
  AccessPolicyMatrixRuleDto,
  AccessPolicyUserContext,
  buildAccessPolicyUserContext
} from '../_models/access-policy';
import {User} from '../_models/user';

@Injectable({
  providedIn: 'root'
})
export class AccessPolicyService {
  private readonly policySubject = new BehaviorSubject<AccessPolicyDto | null>(null);
  private pendingRequest$: Observable<AccessPolicyDto | null> | null = null;

  readonly policy$ = this.policySubject.asObservable();

  constructor(private readonly http: HttpClient) {}

  get policy(): AccessPolicyDto | null {
    return this.policySubject.value;
  }

  ensureLoaded(): Observable<AccessPolicyDto | null> {
    if (this.policy) {
      return of(this.policy);
    }

    if (this.pendingRequest$) {
      return this.pendingRequest$;
    }

    this.pendingRequest$ = this.http.get<AccessPolicyDto>(`${apiUrl}/access-policy`, {withCredentials: true}).pipe(
      tap((policy) => this.policySubject.next(policy)),
      map((policy) => policy ?? null),
      catchError(() => of(null)),
      tap(() => {
        this.pendingRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.pendingRequest$;
  }

  refresh(): Observable<AccessPolicyDto | null> {
    this.clear();
    return this.ensureLoaded();
  }

  clear(): void {
    this.pendingRequest$ = null;
    this.policySubject.next(null);
  }

  can(resource: string,
      action: string,
      ctx: AccessPolicyContext = {},
      user?: User | AccessPolicyUserContext | null): boolean {
    return this.evaluate(resource, action, ctx, user).allowed;
  }

  evaluate(resource: string,
           action: string,
           ctx: AccessPolicyContext = {},
           user?: User | AccessPolicyUserContext | null): AccessPolicyDecision {
    const policy = this.policy;
    const userContext = this.normalizeUserContext(user, ctx);

    if (!policy || !policy.matrix?.length || !userContext?.effectiveRole) {
      return this.deny(resource, action, policy?.evaluation?.defaultEffect ?? 'deny');
    }

    for (let index = 0; index < policy.matrix.length; index += 1) {
      const rule = policy.matrix[index];
      if (!this.matchesRule(rule, resource, action, ctx, userContext, policy)) {
        continue;
      }

      const effect = rule.effect || policy.evaluation?.defaultEffect || 'deny';
      return {
        allowed: effect.toLowerCase() === 'allow',
        effect,
        resource,
        action,
        matchedRuleIndex: index,
        matchedCondition: rule.condition ?? null
      };
    }

    return this.deny(resource, action, policy.evaluation?.defaultEffect ?? 'deny');
  }

  private normalizeUserContext(user?: User | AccessPolicyUserContext | null,
                               ctx: AccessPolicyContext = {}): AccessPolicyUserContext | null {
    if (!user) {
      return null;
    }

    if ('company' in user || 'trade' in user) {
      return buildAccessPolicyUserContext(user as User, ctx.companyId ?? null);
    }

    return user as AccessPolicyUserContext;
  }

  private matchesRule(rule: AccessPolicyMatrixRuleDto,
                      resource: string,
                      action: string,
                      ctx: AccessPolicyContext,
                      user: AccessPolicyUserContext,
                      policy: AccessPolicyDto): boolean {
    if (!this.equalsValue(rule.resource, resource)) {
      return false;
    }

    if (!this.equalsValue(rule.action, action)) {
      return false;
    }

    if (!this.matchesField(rule.projectRole, ctx.projectRole)) {
      return false;
    }

    if (!this.matchesField(rule.boardRole, ctx.boardRole)) {
      return false;
    }

    if (!this.matchesField(rule.taskType, ctx.taskType)) {
      return false;
    }

    if (!rule.condition) {
      return true;
    }

    const predicate = policy.predicates?.[rule.condition];
    if (!predicate?.logic) {
      return false;
    }

    return this.isTruthy(this.resolveLogic(predicate.logic, {user, ctx}));
  }

  private matchesField(expectedValues: string[] | null | undefined, actualValue: unknown): boolean {
    if (!expectedValues?.length) {
      return true;
    }

    if (actualValue == null) {
      return false;
    }

    return expectedValues.some(expectedValue => this.equalsValue(expectedValue, actualValue));
  }

  private resolveLogic(node: AccessPolicyLogicNode, data: { user: AccessPolicyUserContext; ctx: AccessPolicyContext }): unknown {
    if (node == null || typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
      return node;
    }

    if (Array.isArray(node)) {
      return node.map(item => this.resolveLogic(item, data));
    }

    const entries = Object.entries(node);
    if (entries.length !== 1) {
      return node;
    }

    const [operator, value] = entries[0];

    switch (operator) {
      case 'var':
        return this.resolveVar(value, data);
      case '==':
        return this.comparePair(value, data, (left, right) => this.equalsValue(left, right));
      case '!=':
        return this.comparePair(value, data, (left, right) => !this.equalsValue(left, right));
      case 'in':
        return this.resolveIn(value, data);
      case 'and':
        return this.resolveBooleanArray(value, data, 'and');
      case 'or':
        return this.resolveBooleanArray(value, data, 'or');
      default:
        return node;
    }
  }

  private resolveVar(value: AccessPolicyLogicNode, data: { user: AccessPolicyUserContext; ctx: AccessPolicyContext }): unknown {
    const path = Array.isArray(value) ? value[0] : value;
    if (typeof path !== 'string' || !path.trim()) {
      return null;
    }

    return path
      .split('.')
      .reduce<unknown>((currentValue, pathSegment) => {
        if (currentValue == null || typeof currentValue !== 'object') {
          return null;
        }

        return (currentValue as Record<string, unknown>)[pathSegment];
      }, data);
  }

  private comparePair(value: AccessPolicyLogicNode,
                      data: { user: AccessPolicyUserContext; ctx: AccessPolicyContext },
                      comparator: (left: unknown, right: unknown) => boolean): boolean {
    if (!Array.isArray(value) || value.length < 2) {
      return false;
    }

    const [left, right] = value;
    return comparator(this.resolveLogic(left, data), this.resolveLogic(right, data));
  }

  private resolveIn(value: AccessPolicyLogicNode, data: { user: AccessPolicyUserContext; ctx: AccessPolicyContext }): boolean {
    if (!Array.isArray(value) || value.length < 2) {
      return false;
    }

    const [candidateNode, haystackNode] = value;
    const candidate = this.resolveLogic(candidateNode, data);
    const haystack = this.resolveLogic(haystackNode, data);

    if (Array.isArray(haystack)) {
      return haystack.some(item => this.equalsValue(item, candidate));
    }

    if (typeof haystack === 'string') {
      return haystack.includes(String(candidate ?? ''));
    }

    return false;
  }

  private resolveBooleanArray(value: AccessPolicyLogicNode,
                              data: { user: AccessPolicyUserContext; ctx: AccessPolicyContext },
                              mode: 'and' | 'or'): boolean {
    if (!Array.isArray(value)) {
      return false;
    }

    const resolved = value.map(item => this.resolveLogic(item, data));
    return mode === 'and'
      ? resolved.every(item => this.isTruthy(item))
      : resolved.some(item => this.isTruthy(item));
  }

  private isTruthy(value: unknown): boolean {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return !!value;
  }

  private equalsValue(left: unknown, right: unknown): boolean {
    if (left === right) {
      return true;
    }

    if (left == null || right == null) {
      return false;
    }

    return String(left) === String(right);
  }

  private deny(resource: string, action: string, effect: string): AccessPolicyDecision {
    return {
      allowed: effect.toLowerCase() === 'allow',
      effect,
      resource,
      action,
      matchedRuleIndex: null,
      matchedCondition: null
    };
  }
}
